import { FunctionComponent, useState } from 'react';
import {
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNotification } from '@starjack/features/notifications';
import {
    IntegrationTokenError,
    postIntegrationToken,
} from '@/api/v2/liftOperator';
import { IntegrationTokenResponse } from '@/models/liftoperator';
import { LiftOperatorFormCard } from './LiftOperatorFormCard';

type IntegrationTokenCardProps = {
    liftOperatorId: string;
};

const INVALID_URL_MESSAGE =
    'Bitte eine gültige Shop-URL eingeben: beginnt mit https://, ohne Parameter (?…) oder Anker (#…).';

const isInvalidUrlError = (error: IntegrationTokenError | null) =>
    error?.code === 'invalid_integration_url' || error?.code === 'notnull';

const errorMessageFor = (error: IntegrationTokenError | null): string => {
    if (isInvalidUrlError(error)) {
        return INVALID_URL_MESSAGE;
    }
    if (error?.code === 'liftoperator_not_found') {
        return 'Die Bergbahn wurde nicht gefunden. Bitte die Seite neu laden.';
    }
    if (error?.status === 401) {
        return 'Die Sitzung ist abgelaufen. Bitte neu anmelden.';
    }
    if (error?.status === 403) {
        return 'Nur Admins dürfen Integrationstoken erzeugen.';
    }
    return 'Das Token konnte nicht erzeugt werden. Bitte erneut versuchen.';
};

/**
 * Lets admins issue an integration token for the shop of a lift operator.
 * The token only lives in this component's state: it is neither part of the
 * lift operator save payload nor of the global Redux store.
 */
export const IntegrationTokenCard: FunctionComponent<
    IntegrationTokenCardProps
> = ({ liftOperatorId }) => {
    const [integrationUrl, setIntegrationUrl] = useState('');
    const [result, setResult] = useState<IntegrationTokenResponse | null>(null);
    const [urlInvalid, setUrlInvalid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { addNotification } = useNotification();

    const onGenerate = async () => {
        setUrlInvalid(false);
        setResult(null);
        setIsLoading(true);

        let response: IntegrationTokenResponse | null = null;
        let error: IntegrationTokenError | null = null;
        try {
            [response, error] = await postIntegrationToken({
                liftOperatorId,
                integrationUrl: integrationUrl.trim(),
            });
        } catch {
            // network failure, reported with the generic message below
        } finally {
            setIsLoading(false);
        }

        if (response) {
            setResult(response);
            setIntegrationUrl(response.integrationUrl);
            return;
        }

        setUrlInvalid(isInvalidUrlError(error));
        addNotification({
            message: errorMessageFor(error),
            type: 'error',
        });
    };

    const onCopy = () => {
        if (!result) return;

        navigator.clipboard
            .writeText(result.integrationToken)
            .then(() => {
                addNotification({
                    message: 'Token in die Zwischenablage kopiert.',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Kopieren nicht möglich. Bitte das Token manuell markieren und kopieren.',
                    type: 'error',
                });
            });
    };

    return (
        <LiftOperatorFormCard title='Integrationstoken'>
            <Stack spacing={3}>
                <Typography variant='body2'>
                    Das Token wird nicht gespeichert. Jedes erzeugte Token
                    bleibt gültig; für einen neuen Shop einfach ein neues
                    erzeugen.
                </Typography>

                <Stack direction='row' spacing={2} alignItems='flex-start'>
                    <TextField
                        fullWidth
                        name='integrationTokenShopUrl'
                        label='Shop-URL'
                        placeholder='https://www.bergbahn.at/'
                        value={integrationUrl}
                        error={urlInvalid}
                        helperText={
                            urlInvalid ? INVALID_URL_MESSAGE : undefined
                        }
                        onChange={(event) => {
                            setIntegrationUrl(event.target.value);
                            setResult(null);
                            setUrlInvalid(false);
                        }}
                        onKeyDown={(event) => {
                            if (
                                event.key === 'Enter' &&
                                integrationUrl.trim()
                            ) {
                                event.preventDefault();
                                onGenerate();
                            }
                        }}
                    />
                    <LoadingButton
                        variant='contained'
                        onClick={onGenerate}
                        loading={isLoading}
                        disabled={!integrationUrl.trim()}
                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto', py: 2 }}
                    >
                        Token erzeugen
                    </LoadingButton>
                </Stack>

                {result ? (
                    <TextField
                        fullWidth
                        multiline
                        name='integrationToken'
                        label={`Integrationstoken für ${result.integrationUrl}`}
                        value={result.integrationToken}
                        InputProps={{
                            readOnly: true,
                            sx: { fontFamily: 'monospace', fontSize: 13 },
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <Tooltip title='Kopieren'>
                                        <IconButton
                                            aria-label='Token kopieren'
                                            onClick={onCopy}
                                            edge='end'
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        onFocus={(event) => event.target.select()}
                    />
                ) : null}
            </Stack>
        </LiftOperatorFormCard>
    );
};
